"use client"

import { useState, useEffect } from "react"
import { MemoryList } from "./memory-list"
import { MemoryDetailPanel } from "./memory-detail-panel"
import type { SemanticMemory } from "@/lib/memory/semantic/types"
// </CHANGE>
import useSWR, { mutate } from "swr"

export function BrainPageClient() {
  const [memories, setMemories] = useState<SemanticMemory[]>([])
  const [selectedMemory, setSelectedMemory] = useState<SemanticMemory | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const {
    data,
    error,
    mutate: revalidate,
  } = useSWR<{ memories: SemanticMemory[] }>(
    "/api/brain/memories",
    async (url) => {
      const response = await fetch(url)
      if (!response.ok) throw new Error("Failed to fetch memories")
      return response.json()
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  useEffect(() => {
    if (data) {
      setMemories(data.memories || [])
      setIsLoading(false)
    } else if (error) {
      console.error("[BrainPage] Failed to fetch memories:", error)
      setIsLoading(false)
    }
  }, [data, error])

  const fetchMemories = async () => {
    await revalidate()
  }

  const handleEdit = async (id: string, updates: Partial<SemanticMemory>) => {
    try {
      const response = await fetch(`/api/brain/memories/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      })

      if (response.ok) {
        await revalidate()
        if (selectedMemory?.id === id) {
          const updated = await response.json()
          setSelectedMemory(updated.memory)
        }
      }
    } catch (error) {
      console.error("[BrainPage] Failed to update memory:", error)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this memory?")) return

    try {
      const response = await fetch(`/api/brain/memories/${id}`, {
        method: "DELETE",
      })

      if (response.ok) {
        await revalidate()
        if (selectedMemory?.id === id) {
          setSelectedMemory(null)
        }
      }
    } catch (error) {
      console.error("[BrainPage] Failed to delete memory:", error)
    }
  }

  const handleArchive = async (id: string) => {
    await handleEdit(id, { isArchived: true })
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left: Memory List */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* </CHANGE> */}

        <MemoryList
          memories={memories}
          isLoading={isLoading}
          searchQuery={searchQuery}
          selectedCategory={selectedCategory}
          selectedMemoryId={selectedMemory?.id}
          onSearchChange={setSearchQuery}
          onCategoryChange={setSelectedCategory}
          onSelectMemory={setSelectedMemory}
          onRefresh={fetchMemories}
        />
      </div>

      {/* Middle: Detail Panel (when memory selected) */}
      {selectedMemory && (
        <MemoryDetailPanel
          memory={selectedMemory}
          onEdit={(updates) => handleEdit(selectedMemory.id, updates)}
          onDelete={() => handleDelete(selectedMemory.id)}
          onArchive={() => handleArchive(selectedMemory.id)}
          onClose={() => setSelectedMemory(null)}
        />
      )}

      {/* Right: System Health panel removed to reduce cognitive load */}
      {/* </CHANGE> */}
    </div>
  )
}

export async function revalidateBrainMemories() {
  await mutate("/api/brain/memories")
}
